/**
 * InstallPrompt — Subtle banner encouraging PWA installation.
 * Sprint 6, SDD §7.5
 *
 * Listens for the `beforeinstallprompt` event (Chrome/Edge) and displays
 * a dismissible banner. On iOS Safari (which doesn't fire the event),
 * shows manual instructions on first visit.
 *
 * The banner is only shown once per session unless the user hasn't
 * installed the app. Dismissal is remembered in localStorage.
 */

import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'sh-install-dismissed';
const IOS_DISMISS_KEY = 'sh-ios-install-dismissed';

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Already installed as PWA — don't show
    if (isStandalone()) return;

    // Already dismissed — don't show
    if (localStorage.getItem(DISMISS_KEY) === 'true') {
      setDismissed(true);
      return;
    }

    // Listen for the browser's install prompt event
    function handleBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS Safari: show manual instructions if not already dismissed
    if (isIOS() && localStorage.getItem(IOS_DISMISS_KEY) !== 'true') {
      setShowIOSPrompt(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
    // Either way, dismiss the banner
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, 'true');
    localStorage.setItem(IOS_DISMISS_KEY, 'true');
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
  }, []);

  // Nothing to show
  if (dismissed) return null;
  if (!deferredPrompt && !showIOSPrompt) return null;

  return (
    <div
      role="banner"
      aria-label="Install app"
      className="fixed bottom-0 left-0 right-0 z-[1000] py-3 px-5 bg-surface-raised border-t border-edge-default flex items-center justify-center gap-3 shadow-lg safe-bottom"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-content-primary">Install ShiftHappens</div>
        {showIOSPrompt && !deferredPrompt ? (
          <div className="text-xs text-content-secondary mt-0.5">
            Tap the share button, then &quot;Add to Home Screen&quot;
          </div>
        ) : (
          <div className="text-xs text-content-secondary mt-0.5">Add to your home screen for quick access</div>
        )}
      </div>
      {deferredPrompt && (
        <button
          type="button"
          onClick={handleInstall}
          className="py-2 px-4 text-[13px] font-semibold text-surface-base bg-action-primary border-none rounded-md cursor-pointer whitespace-nowrap"
        >
          Install
        </button>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        className="bg-transparent border-none text-content-tertiary text-xl cursor-pointer px-1 leading-none"
      >
        ×
      </button>
    </div>
  );
}
